/**
 * EXPANSION STATE STORAGE UTILITY
 *
 * Manages test case and test step expand/collapse state persistence
 * in localStorage with LRU (Least Recently Used) eviction policy.
 *
 * Uses a single localStorage key ('test-expansion-state') storing a map
 * of scenario entries. Each entry tracks which test cases and steps are
 * expanded, plus a lastAccessed timestamp for LRU eviction.
 *
 * Capped at MAX_ENTRIES (50) scenarios to prevent unbounded growth.
 * When the limit is exceeded, the least recently accessed scenario
 * entry is automatically removed.
 */

const STORAGE_KEY = 'test-expansion-state'
const MAX_ENTRIES = 50

/**
 * Reads the full expansion state map from localStorage.
 * Returns an empty entries object if nothing is stored or on parse error.
 * @returns {{ entries: Object }} The stored expansion state
 */
function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed.entries === 'object') {
        return parsed
      }
    }
  } catch (e) {
    console.warn('Failed to read expansion state from localStorage:', e)
  }
  return { entries: {} }
}

/**
 * Writes the full expansion state map to localStorage.
 * @param {Object} data - The expansion state object to persist
 */
function writeStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('Failed to write expansion state to localStorage:', e)
  }
}

/**
 * Evicts the least recently accessed entry if the entry count exceeds MAX_ENTRIES.
 * Mutates the entries object in place.
 * @param {Object} entries - The scenario entries map
 */
function evictIfNeeded(entries) {
  const keys = Object.keys(entries)
  if (keys.length <= MAX_ENTRIES) return

  // Find the entry with the smallest lastAccessed timestamp
  let oldestKey = keys[0]
  let oldestTime = entries[keys[0]].lastAccessed || 0

  for (let i = 1; i < keys.length; i++) {
    const time = entries[keys[i]].lastAccessed || 0
    if (time < oldestTime) {
      oldestTime = time
      oldestKey = keys[i]
    }
  }

  delete entries[oldestKey]
}

/**
 * GET EXPANSION STATE
 *
 * Retrieves the saved expansion state for a specific scenario.
 * Updates the lastAccessed timestamp so this entry is less likely to be evicted.
 *
 * @param {string|number} scenarioId - The scenario ID to look up
 * @returns {{ testCases: number[], testSteps: number[] }} The expansion arrays
 */
export function getExpansionState(scenarioId) {
  if (!scenarioId) return { testCases: [], testSteps: [] }

  const data = readStorage()
  const key = String(scenarioId)
  const entry = data.entries[key]

  if (entry) {
    // Update lastAccessed timestamp on read
    entry.lastAccessed = Date.now()
    writeStorage(data)
    return {
      testCases: Array.isArray(entry.testCases) ? entry.testCases : [],
      testSteps: Array.isArray(entry.testSteps) ? entry.testSteps : []
    }
  }

  return { testCases: [], testSteps: [] }
}

/**
 * SAVE EXPANSION STATE
 *
 * Persists the expansion state for a specific scenario.
 * Creates the entry if it doesn't exist, updates it if it does.
 * Evicts the oldest entry if the MAX_ENTRIES limit is exceeded.
 *
 * @param {string|number} scenarioId - The scenario ID to save for
 * @param {number[]} testCases - Array of expanded test case IDs
 * @param {number[]} testSteps - Array of expanded test step IDs
 */
export function saveExpansionState(scenarioId, testCases, testSteps) {
  if (!scenarioId) return

  const data = readStorage()
  const key = String(scenarioId)

  data.entries[key] = {
    testCases: testCases || [],
    testSteps: testSteps || [],
    lastAccessed: Date.now()
  }

  // Evict oldest entry if we've exceeded the limit
  evictIfNeeded(data.entries)

  writeStorage(data)
}
